import type { CityConfig } from "../city-template/types";

export const burlingtonConfig: CityConfig = {
  city: "Burlington",
  state: "North Carolina",
  stateAbbr: "NC",
  slug: "burlington",
  route: "/cities/burlington",
  title: "Emergency Passport Burlington, NC | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Burlington, NC — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Alamance County and the Piedmont Triad-Triangle corridor. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Burlington, NC",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Burlington and Alamance County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20view%20of%20Burlington%20North%20Carolina%20downtown%20at%20golden%20hour%20with%20historic%20Alamance%20County%20courthouse%2C%20charming%20tree-lined%20streets%20with%20modern%20retail%20and%20local%20shops%2C%20warm%20afternoon%20sunlight%20over%20the%20Piedmont%20region%2C%20lush%20green%20rolling%20hills%20in%20the%20background%2C%20clean%20blue%20sky%20with%20soft%20clouds%2C%20professional%20cityscape%20photography%20with%20vibrant%20warm%20colors%20and%20suburban%20southern%20charm%2C%20no%20text%20or%20signs%20on%20buildings%2C%20clean%20modern%20town%20scene&width=1800&height=700&seq=burlington-hero-nc-001&orientation=landscape",
  heroImageAlt: "Burlington North Carolina downtown with historic Alamance County courthouse and Piedmont hills",
  geoPosition: "36.0957;-79.4378",
  geoPlacename: "Burlington, North Carolina",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree Street NW, Suite 1400, Atlanta, GA 30303",
    distance: "Located in downtown Atlanta — approximately 280 miles from Burlington via I-85",
  },
  nearbyCitiesZips: [
    { name: "Burlington", zips: "27215, 27216, 27217" },
    { name: "Graham", zips: "27253" },
    { name: "Mebane", zips: "27302" },
    { name: "Elon", zips: "27244" },
    { name: "Haw River", zips: "27258" },
    { name: "Snow Camp", zips: "27349" },
    { name: "Saxapahaw", zips: "27253" },
    { name: "Swepsonville", zips: "27359" },
    { name: "Glen Raven", zips: "27217" },
    { name: "Alamance", zips: "27201" },
    { name: "Gibsonville", zips: "27249" },
    { name: "Liberty", zips: "27298" },
    { name: "Whitsett", zips: "27377" },
    { name: "Hillsborough", zips: "27278" },
    { name: "Chapel Hill", zips: "27514–27517" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Burlington departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Alamance County Hub", desc: "Serving Burlington, Graham, and the Piedmont Triad-Triangle corridor" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Greensboro, NC", route: "/cities/greensboro" },
    { name: "Fast passport renewal in Durham, NC", route: "/cities/durham" },
    { name: "Emergency passport help in Raleigh, NC", route: "/cities/raleigh" },
    { name: "Urgent passport services in Chapel Hill, NC", route: "/cities/chapel-hill" },
    { name: "Same-day passport help in Winston-Salem, NC", route: "/cities/winston-salem" },
    { name: "Passport courier services in Charlotte, NC", route: "/cities/charlotte" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Burlington remotely via secure courier. We do not have a walk-in office in Burlington — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Alamance County and the Piedmont Triad-Triangle corridor travelers.",
  fpcOfficeDistance: "Serving all of Alamance County and the Piedmont Triad-Triangle corridor including Graham and Mebane",
  serviceAreaIntro: "From downtown Burlington to the historic towns of Graham and Mebane, from Elon University to Haw River and Saxapahaw — Fast Passport Center serves the entire Alamance County region. We regularly help travelers from Snow Camp, Swepsonville, Glen Raven, and Gibsonville get their passports fast.",
  nearbyCitiesIntro: "We serve all Burlington neighborhoods and the surrounding Alamance County area including Graham, Mebane, Elon, Haw River, Snow Camp, Saxapahaw, Swepsonville, Glen Raven, Alamance, Gibsonville, Liberty, Whitsett, Hillsborough, and Chapel Hill. If you are anywhere in Alamance, Orange, or Guilford counties, we have you covered.",
  howItWorksIntro: "Most Burlington travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Burlington travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Burlington and Alamance County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Burlington? Here are the most common ones we hear from Alamance County travelers.",
  statePageRoute: "/states/north-carolina",
  statePageName: "North Carolina",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Burlington international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Rachel M.",
      location: "Burlington, NC",
      rating: 5,
      text: "My passport expired the day before our cruise to the Caribbean. Fast Passport Center had my renewal in 36 hours. Never had to leave Alamance County. Incredible service.",
      service: "Passport Renewal",
    },
    {
      name: "James T.",
      location: "Graham, NC",
      rating: 5,
      text: "Emergency passport for a family medical emergency in London. Got it in 30 hours. The team was incredibly responsive and compassionate. True lifesavers.",
      service: "Emergency Passport",
    },
    {
      name: "Michelle D.",
      location: "Mebane, NC",
      rating: 5,
      text: "Business trip to Toronto came up with 6 days notice. They got me a passport in 4 business days. The communication was outstanding throughout the process.",
      service: "Emergency Passport",
    },
    {
      name: "David L.",
      location: "Elon, NC",
      rating: 5,
      text: "First passport for my son before his study abroad in Spain. Fast Passport Center helped us navigate every step. Ready in 5 days with zero stress. Truly grateful.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "burlington-1",
      name: "Burlington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "540 W Front St",
      city: "Burlington",
      state: "NC",
      zip: "27215",
      phone: "(336) 228-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Burlington facility. High volume — appointments strongly recommended. Call ahead to confirm passport acceptance.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-2",
      name: "Graham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "108 W Elm St",
      city: "Graham",
      state: "NC",
      zip: "27253",
      phone: "(336) 226-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "North of Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Graham and Elon residents. Less crowded than downtown Burlington.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-3",
      name: "Mebane Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 S Fifth St",
      city: "Mebane",
      state: "NC",
      zip: "27302",
      phone: "(919) 563-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "East of Burlington, Orange County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Mebane and eastern Alamance County. Full passport services. Appointments required.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-4",
      name: "Haw River Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "113 W Main St",
      city: "Haw River",
      state: "NC",
      zip: "27258",
      phone: "(336) 578-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "South of Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Haw River and southern Alamance County. Call for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-5",
      name: "Alamance County Clerk of Court",
      type: "Court Clerk Passport Acceptance",
      address: "212 W Elm St",
      city: "Graham",
      state: "NC",
      zip: "27253",
      phone: "(336) 570-5200",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Alamance County Courthouse, Graham",
      website: "https://www.alamance-nc.com/",
      websiteLabel: "Alamance County Clerk",
      notes: "Accepts passport applications. Good alternative to post office with extended hours. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "burlington-6",
      name: "Gibsonville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "109 E Main St",
      city: "Gibsonville",
      state: "NC",
      zip: "27249",
      phone: "(336) 449-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "East of Burlington, Guilford County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Gibsonville and eastern Alamance County border. Full passport services.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 4.5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Burlington photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Alamance County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Burlington applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Alamance County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Burlington travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Burlington." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Burlington address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Burlington before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Burlington for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 4.5 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Burlington?", a: "Fast Passport Center offers fast passport renewal in Burlington in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Burlington?", a: "The nearest regional passport agency to Burlington is the Atlanta Passport Agency, approximately 280 miles away via I-85. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Burlington without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Burlington?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — approximately 4.5 hours from Burlington — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Alamance County residents." },
    { q: "Do you serve the entire Triad-Triangle corridor?", a: "Absolutely. Fast Passport Center serves the entire Piedmont Triad-Triangle corridor including Burlington, Graham, Mebane, Elon, Hillsborough, Chapel Hill, and Durham. Our courier service works seamlessly from any address in Alamance, Orange, or Guilford counties." },
    { q: "Can I expedite a child passport in Burlington?", a: "Yes. Fast Passport Center expedites child passports in Burlington for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Alamance County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Burlington with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Burlington", address: "1445 S Church St, Burlington, NC 27215", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Burlington", address: "2305 S Church St, Burlington, NC 27215", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart Supercenter — Burlington", address: "314 E Burlington Blvd, Burlington, NC 27215", hours: "Mon–Sun: 6AM–11PM" },
    { name: "FedEx Office — Burlington", address: "2009 S Church St, Burlington, NC 27215", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "CVS Pharmacy — Graham", address: "108 W Elm St, Graham, NC 27253", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Mebane", address: "1020 S Fifth St, Mebane, NC 27302", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Mebane", address: "1306 S Fifth St, Mebane, NC 27302", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Mebane", address: "1318 S Fifth St, Mebane, NC 27302", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "NC DMV — Burlington",
      address: "2521 S Church St, Burlington, NC 27215",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "NC DMV — Graham",
      address: "108 W Elm St, Graham, NC 27253",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and passport-related ID services. Appointment strongly recommended.",
      featured: false,
    },
    {
      name: "NC DMV — Mebane",
      address: "1000 S Fifth St, Mebane, NC 27302",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Orange County and eastern Alamance County residents. REAL ID available.",
      featured: false,
    },
    {
      name: "NC DMV — Hillsborough",
      address: "1185 S Nash St, Hillsborough, NC 27278",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Orange County and western Durham County. REAL ID available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "RDU",
      name: "Raleigh-Durham International Airport",
      distance: "50 miles east of Burlington",
      address: "2400 John Brantley Blvd, Morrisville, NC 27560",
      note: "Primary airport for the Triangle region. Direct flights to major domestic hubs including London, Paris, and Cancun. Major international hub for Alamance County travelers.",
      flightUrl: "https://www.rdu.com/",
      flightLabel: "RDU Airport",
      featured: true,
    },
    {
      code: "GSO",
      name: "Piedmont Triad International Airport",
      distance: "50 miles west of Burlington",
      address: "1000 Ted Johnson Pkwy, Greensboro, NC 27409",
      note: "Alternative airport for the Triad region. Additional domestic routes with American and Delta.",
      flightUrl: "https://www.flyfrompti.com/",
      flightLabel: "GSO Airport",
      featured: false,
    },
    {
      code: "CLT",
      name: "Charlotte Douglas International Airport",
      distance: "130 miles southwest of Burlington",
      address: "5501 Josh Birmingham Pkwy, Charlotte, NC 28208",
      note: "Major international hub. American Airlines hub with direct flights to Europe, Mexico, and the Caribbean. Consider for international travel from Alamance County.",
      flightUrl: "https://www.cltairport.com/",
      flightLabel: "CLT Airport",
      featured: false,
    },
  ],
};